<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Currency ({{ rules.name }})</span>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>

      <v-card-text class="resizable-card__body">
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-row dense>
          <v-col cols="12" sm="4" md="4">
            <r-currency-kind-dropdown :model.sync="model.kind" />
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <r-currency-group-dropdown :model.sync="model.group"/>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-text-field label="Order:" v-model.number="model.order"/>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" sm="4" md="4">
            <v-btn
                v-if="!!model.search"
                @click="onSearch"
                small
                color="success"
                class="my-1 fill-width"
            >
              Search
            </v-btn>
            <v-btn
                v-else
                @click="onSearch"
                small
                outlined
                color="primary"
                class="my-1 fill-width"
            >
              Search
            </v-btn>
          </v-col>

          <v-col cols="12" md="4" sm="4">
            <v-btn
                v-if="!!model.deprecate"
                @click="onDeprecate"
                small
                color="success"
                class="my-1 fill-width"
            >
              Deprecate Config
            </v-btn>
            <v-btn
                v-else
                @click="onDeprecate"
                small
                outlined
                color="primary"
                class="my-1 fill-width"
            >
              Deprecate Config
            </v-btn>
          </v-col>
        </v-row>

        <v-row dense class="mb-2 mt-4">
          <v-divider/>
        </v-row>

        <v-expansion-panels>
          <r-guard-panel :model.sync="model.guard"/>
          <r-currency-formula-panel :items.sync="model.formulas" title="Formulas"/>
          <r-item-panel :items.sync="model.customers " title="Customers"/>
          <r-item-panel :items.sync="model.emitters " title="Emitters"/>
          <r-panel title="Map Group">
            <r-map-group-dropdown :model.sync="model.mapGroup" />
          </r-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn
            @click="save"
            color="blue darken-1"
            text
            v-if="!lockedAssess || masterAccess"
        >
          Save
        </v-btn>
      </v-card-actions>

      <div
          class="resize-handle"
          @mousedown.stop.prevent="startResize"
      ></div>
    </v-card>

    <r-search-dialog ref="r-search-dialog"/>
    <r-currency-deprecate-dialog ref="r-currency-deprecate-dialog"/>
    <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import { ViewRules } from "@/common/ViewRules";
import { CurrencyConfig } from "@/modules/redactor/models/currency/CurrencyConfig";
import { RedactorUtils } from "@/modules/redactor/core/RedactorUtils";

import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RGuardPanel from "@/modules/redactor/views/guard/r-guard-panel";
import RCurrencyKindDropdown from "@/modules/redactor/views/currency-kind/r-currency-kind-dropdown.vue";
import RCurrencyGroupDropdown from "@/modules/redactor/views/currency-group/r-currency-group-dropdown.vue";
import RCurrencyFormulaPanel from "@/modules/redactor/views/currency/r-currency-formula-panel.vue";
import RCurrencyDeprecateDialog from "@/modules/redactor/views/currency/r-currency-deprecate-dialog.vue";
import RSearchDialog from "@/modules/redactor/views/search/r-search-dialog.vue";
import RItemPanel from "@/modules/redactor/views/item/r-item-panel.vue";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";
import {MapAddonGroupModel} from "@/modules/redactor/models/map-addon/core/MapAddonGroupModel";
import RMapGroupDropdown from "@/modules/redactor/views/map-group/r-map-group-dropdown.vue";
import RPanel from "@/modules/redactor/views/utils/r-panel.vue";

export default {
  name: "r-currency-config-dialog",
  components: {
    RPreviewDialog,
    RSearchDialog,
    RCurrencyDeprecateDialog,
    RCurrencyFormulaPanel,
    RAuthorize,
    RCurrencyKindDropdown,
    RCurrencyGroupDropdown,
    RGuardPanel,
    RItemPanel,
    RMapGroupDropdown,
    RPanel
  },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(CurrencyConfig),
    rules: ViewRules.READONLY,

    dialogWidth: 575,
    dialogHeight: 598,

    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 575,
    resizeStartHeight: 598,
    viewportPadding: 32
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new CurrencyConfig();

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve && this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject && this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },
    onSearch() {
      let data = this.model.search ? this.model.search.clone() : null;
      this.$refs["r-search-dialog"]
          .show(ViewRules.UPDATE, data)
          .then(data => {
            this.model.search = data;
          })
          .catch(() => {});
    },
    onDeprecate() {
      let data = this.model.deprecate ? this.model.deprecate.clone() : null;
      this.$refs["r-currency-deprecate-dialog"]
          .show(ViewRules.UPDATE, data)
          .then(data => {
            this.model.deprecate = data;
          })
          .catch(() => {});
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;

      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 460;
      const minHeight = 380;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, CurrencyConfig)
        .then((result) => {
          Object.assign(this.model, result);
        })
        .catch((e) => {
          this.$alert("Failed to parse config!" + e.message);
        });
    }
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.fill-width {
  width: 100%;
}
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>